import React, { useState, useEffect } from 'react';
import {
  Flex,
  Box,
  Panel,
  Text,
  theme,
  Toggle,
  Spinner,
  Alert,
} from '@xometry/xometry_loft';
import quotesApi from '../../../../services/quotes';

const TeamSharingDefaults = ({
  userId,
  orgId,
  isStaff,
  pageReadOnly,
  onOnboardingActionCompleted,
}: {
  userId: string;
  orgId: string;
  isStaff: boolean;
  pageReadOnly: boolean;
  onOnboardingActionCompleted: () => void;
}) => {
  const [isSharingActive, setIsSharingActive] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);

  const setAutoShareSetting = async (active: boolean) => {
    const setQuoteOrgSharingActive = await quotesApi.setQuoteOrgSetting(
      userId,
      orgId,
      'auto-share',
      active
    );
    const setToolOrgSharingActive = await quotesApi.setToolOrgSetting(
      userId,
      orgId,
      'auto-share',
      active
    );

    return (
      Boolean(setQuoteOrgSharingActive) && Boolean(setToolOrgSharingActive)
    );
  };

  useEffect(() => {
    setIsLoading(true);
    const fetchQuoteOrgSharingDefault = async () => {
      try {
        const getQuoteOrgSharingActive = await quotesApi.getQuoteOrgSetting(
          userId,
          orgId,
          'auto-share'
        );
        const getToolNotificationSettings = await quotesApi.getToolOrgSettings(
          userId,
          orgId
        );
        let activeValue;
        if (getToolNotificationSettings) {
          const autoShareSetting = getToolNotificationSettings.find(
            (orgSetting) => orgSetting.type === 'auto-share'
          );
          activeValue = autoShareSetting ? autoShareSetting.active : false;
        }

        const autoShareActive =
          Boolean(getQuoteOrgSharingActive) && Boolean(activeValue);
        setIsSharingActive(!!autoShareActive);
        if (autoShareActive === false) {
          // Synchronizes settings: if both aren't true, then they must be false
          await setAutoShareSetting(false);
        }

        setIsError(false);
      } catch (e) {
        console.error(e);
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    };
    fetchQuoteOrgSharingDefault();
  }, []);

  const handleToggleSharing = async (toggleBoolean: boolean) => {
    if (pageReadOnly) return;
    setIsLoading(true);
    try {
      // Per discussions with design, staff cannot set quote sharing on behalf of users.
      // Design wants the toggle to be active, but no action happens when they click it.
      if (isStaff) return;

      const isAutoSharingOn = await setAutoShareSetting(toggleBoolean);
      setIsSharingActive(isAutoSharingOn);
      setIsError(false);

      // Call onboarding callback when sharing is enabled
      if (toggleBoolean && isAutoSharingOn) {
        onOnboardingActionCompleted();
      }
    } catch {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <Flex
        marginBottom={theme.space[4]}
        justifyContent="space-between"
        alignItems="center"
      >
        <Text kind="subpage-title">Sharing</Text>
      </Flex>
      <Panel kind="primary" lineHeight="24px">
        <Flex gap={theme.space[6]} flexDirection="column">
          <Text kind="body-title">Quote, Order, and Tool Sharing</Text>
          {isLoading ? (
            <Spinner />
          ) : (
            <Toggle
              handleChange={(nextCheck: boolean) =>
                handleToggleSharing(nextCheck)
              }
              checked={isSharingActive}
              disabled={pageReadOnly}
              label={
                <Text>
                  Autoshare my new quotes, orders, and tools to this team.
                </Text>
              }
            />
          )}
          {isError && (
            <Alert isOpen={isError} kind={theme.kind.danger}>
              {' '}
              Something went wrong!
            </Alert>
          )}
        </Flex>
      </Panel>
    </Box>
  );
};

TeamSharingDefaults.defaultProps = {
  userId: null,
  orgId: null,
  isStaff: false,
};

export default TeamSharingDefaults;
